import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { apiResetPassword } from "@/lib/api";
import { testIds } from "@/constants/testIds/b2b";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await apiResetPassword(token, password);
      setDone(true);
      toast.success("Password updated — you can sign in now.");
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reset password. Please request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-card" data-testid="reset-password-page">
      <div className="max-w-md mx-auto px-4 md:px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Wholesale portal</p>
        <h1 className="text-2xl md:text-3xl font-semibold mb-1">Set a new password</h1>

        {done ? (
          <div
            data-testid={testIds.resetSuccess}
            className="mt-6 p-5 rounded-md border border-border bg-secondary text-sm"
          >
            <p className="flex items-center gap-2 font-medium mb-1">
              <CheckCircle2 size={16} strokeWidth={1.75} className="text-primary" /> Password updated
            </p>
            <p className="text-muted-foreground">Redirecting you to sign in…</p>
            <Link to="/login" data-testid={testIds.resetGoToLogin} className="inline-block mt-4 text-primary underline text-sm">
              Sign in now
            </Link>
          </div>
        ) : !token ? (
          <div className="mt-6 p-5 rounded-md border border-destructive/40 bg-destructive/5 text-sm" data-testid={testIds.resetError}>
            <p className="text-muted-foreground">
              This reset link is missing its token.{" "}
              <Link to="/forgot-password" className="text-primary underline">Request a new link</Link>.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-8">Choose a new password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground block mb-1.5">New password</label>
                <input
                  data-testid={testIds.resetPassword}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground block mb-1.5">Confirm password</label>
                <input
                  data-testid={testIds.resetConfirm}
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary"
                />
              </div>
              {err && <p className="text-sm text-destructive" data-testid={testIds.resetError}>{err}</p>}
              <button
                data-testid={testIds.resetSubmit}
                type="submit"
                disabled={busy}
                className="w-full py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
